import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import MyLibraryModalUI from '../view/MyLibraryModalUI';
import MyLibraryModalStore from '../store/MyLibraryModalStore';
import {useParams} from 'react-router-dom';
import {
  MediaFile,
  MEDIA_FILES_DOCUMENT,
} from '../../../data/models/LibraryFile/MediaFile';

interface Props {
  myLibraryModalStore?: MyLibraryModalStore;
}

const PAGE_SIZE = 9;

const MyLibraryModalContainer = inject('myLibraryModalStore')(
  observer(({myLibraryModalStore}: Props) => {
    const [files, setFiles] = useState<MediaFile[]>([]);
    const [pages, setPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [storageLimit, setStorageLimit] = useState(0);
    const [sortOptions, setSortOptions] = useState({
      column: 'date_created',
      order: 'desc',
    });
    const [selectedFilter, setSelectedFilter] = useState(
      myLibraryModalStore!.getInitialSelectedFilter(),
    );
    const [search, setSearch] = useState('');
    const [fetchKey, setFetchKey] = useState(0);
    const {organizationId} = useParams();

    useEffect(() => {
      if (organizationId && myLibraryModalStore!.libraryShown) {
        setSelectedFilter(myLibraryModalStore!.getInitialSelectedFilter());
        setFetchKey(k => k + 1);
        myLibraryModalStore!
          .getStorageLimit(organizationId)
          .then(setStorageLimit);
      }
      if (!myLibraryModalStore!.libraryShown) {
        setFiles([]);
        setCurrentPage(1);
        setPages(0);
        setSearch('');
        setSelectedFilter(-1);
      }
    }, [myLibraryModalStore!.libraryShown]);

    useEffect(() => {
      if (myLibraryModalStore!.libraryShown) {
        fetchTapyblVideos();
      }
    }, [search, selectedFilter, currentPage, sortOptions, fetchKey]);

    const onSearch = (value: string) => {
      setCurrentPage(1);
      setSearch(value);
    };

    const fetchTapyblVideos = () => {
      if (organizationId) {
        myLibraryModalStore
          ?.getMediaFiles(
            currentPage - 1,
            organizationId,
            PAGE_SIZE,
            sortOptions.column,
            search,
            selectedFilter,
            sortOptions.order === 'asc',
          )
          .then(res => {
            const rawFiles = (res.data || []) as MediaFile[];
            const shouldExcludeDocs =
              myLibraryModalStore!.getExcludeDocuments() &&
              selectedFilter !== MEDIA_FILES_DOCUMENT;
            const minDurationSeconds =
              myLibraryModalStore!.getMinDurationSeconds() ?? null;
            const filteredFiles = rawFiles.filter(file => {
              const fileType = (file.fileType || '').toLowerCase();
              if (shouldExcludeDocs && fileType.includes('application')) {
                return false;
              }
              if (minDurationSeconds && minDurationSeconds > 0) {
                if (fileType.includes('video')) {
                  const rawDuration =
                    typeof (file as any).duration === 'number'
                      ? (file as any).duration
                      : Number((file as any).duration);
                  if (!Number.isFinite(rawDuration)) {
                    return false;
                  }
                  return rawDuration >= minDurationSeconds;
                }
                // allow images even if min duration is set
                if (fileType.includes('image')) {
                  return true;
                }
                // fallback: reject other types
                return false;
              }
              return true;
            });
            setFiles(filteredFiles);
            setPages(res.pagesCount);
          });
      }
    };

    const onPageChange = (page: number) => {
      setCurrentPage(page);
    };

    const onSort = (selectedColumn: any, sortOrder: any) => {
      setSortOptions({column: selectedColumn, order: sortOrder});
    };

    const onFilterChange = (filter: number) => {
      setCurrentPage(1);
      setSelectedFilter(filter);
    };

    return myLibraryModalStore!.libraryShown ? (
      <MyLibraryModalUI
        refreshFiles={fetchTapyblVideos}
        isLoading={myLibraryModalStore!.isLoading}
        onCancel={() => myLibraryModalStore!.cancelLibrary()}
        files={files}
        type={myLibraryModalStore!.getSourceType()}
        onFileSelect={(files: MediaFile[]) =>
          myLibraryModalStore!.onFileSelect(files)
        }
        pages={pages}
        currentPage={currentPage}
        onPageChange={onPageChange}
        storageLimit={storageLimit}
        onSort={onSort}
        search={search}
        setSearch={onSearch}
        selectedFilter={selectedFilter}
        setSelectedFilter={onFilterChange}
        sortOptions={sortOptions}
        selectMultiple={myLibraryModalStore!.canSelectMultiple}
        preSelectedFiles={myLibraryModalStore!.preSelectedFiles}
        excludeDocuments={myLibraryModalStore!.getExcludeDocuments()}
      />
    ) : null;
  }),
);

export default MyLibraryModalContainer;
